const { registerBlockType } = wp.blocks;
const { RichText, URLInputButton } = wp.blockEditor;
const { Fragment } = wp.element;
const { Button, Icon, PanelBody, ToggleControl, SelectControl } = wp.components;
const { InspectorControls, MediaUpload, MediaUploadCheck } = wp.blockEditor;
const { __ } = wp.i18n;

import PgButton from '../utils/pg-button';

const BlockEdit = ({ attributes, setAttributes }) => {
  const {
    header,
    intro,
    description,
    columns,
    buttons,
    imgUrl,
    altText,
    isDarkMode,
  } = attributes;

  const addColumn = () => {
    const newColumns = [...columns, ''];
    setAttributes({ columns: newColumns });
  };

  const removeColumn = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setAttributes({ columns: newColumns });
  };

  const addButton = () => {
    const newButtons = [
      ...buttons,
      {
        link: '#',
        label: 'New Button',
        buttonClass: `btn ${isDarkMode ? 'btn-light' : 'btn-dark'} btn-solid`,
      },
    ];
    setAttributes({ buttons: newButtons });
  };

  const removeButton = (index) => {
    const newButtons = buttons.filter((_, i) => i !== index);
    setAttributes({ buttons: newButtons });
  };

  const updateButton = (index, updates) => {
    const newButtons = buttons.map((button, i) => {
      if (i === index) {
        return { ...button, ...updates };
      }
      return button;
    });
    setAttributes({ buttons: newButtons });
  };

  const removeImage = () => {
    setAttributes({ imgUrl: '', altText: '' });
  };

  return (
    <Fragment>
      <InspectorControls>
        <PanelBody title={__('Layout Settings')}>
          <SelectControl
            label={__('Section Class')}
            value={attributes.sectionClass}
            options={[
              { label: 'Dark Secondary', value: 'section-dark-secondary' },
              { label: 'Dark Primary', value: 'section-dark-primary' },
              { label: 'Light Primary', value: 'section-light-primary' },
              { label: 'Light Secondary', value: 'section-light-secondary' },
            ]}
            onChange={(value) => setAttributes({ sectionClass: value })}
          />
          <SelectControl
            label={__('Corner Position')}
            value={attributes.cornerPositionClass}
            options={[
              { label: 'None', value: 'corner-disabled' },
              { label: 'Left-Top', value: 'corner-left-top' },
              { label: 'Left-Bottom', value: 'corner-left-bottom' },
              { label: 'Right-Top', value: 'corner-right-top' },
              { label: 'Right-Bottom', value: 'corner-right-bottom' },
            ]}
            onChange={(value) => setAttributes({ cornerPositionClass: value })}
          />
          <SelectControl
            label={__('Corner Background')}
            value={attributes.cornerBackgroundClass}
            options={[
              { label: 'Dark Primary', value: 'corner-on-dark-primary' },
              { label: 'Dark Secondary', value: 'corner-on-dark-secondary' },
              { label: 'Light Primary', value: 'corner-on-light-primary' },
              { label: 'Light Secondary', value: 'corner-on-light-secondary' },
            ]}
            onChange={(value) =>
              setAttributes({ cornerBackgroundClass: value })
            }
          />
        </PanelBody>

        <PanelBody title={__('Image Settings')}>
          <MediaUploadCheck>
            <MediaUpload
              onSelect={(media) => {
                setAttributes({
                  imgUrl: media.url,
                  altText: media.alt || '', // Set alt text from media or empty if not set
                });
              }}
              allowedTypes={['image']}
              value={imgUrl}
              render={({ open }) => (
                <Button isPrimary onClick={open} isSecondary>
                  {imgUrl ? (
                    <>
                      <Icon icon="edit" /> {__('Change Image')}
                    </>
                  ) : (
                    <>
                      <Icon icon="format-image" /> {__('Add Image')}
                    </>
                  )}
                </Button>
              )}
            />
          </MediaUploadCheck>
          {imgUrl && (
            <Button isPrimary isDestructive onClick={removeImage}>
              <Icon icon="trash" /> {__('Remove Image')}
            </Button>
          )}
        </PanelBody>
        <PanelBody title={__('Content Settings')}>
          <Button isPrimary onClick={addColumn}>
            <Icon icon="insert" /> {__('Add Column')}
          </Button>
          <Button isPrimary onClick={addButton}>
            <Icon icon="plus" /> {__('Add Button')}
          </Button>
        </PanelBody>
        <PanelBody title={__('Appearance Settings')}>
          <ToggleControl
            label={__('Dark Mode')}
            checked={isDarkMode}
            onChange={(value) => setAttributes({ isDarkMode: value })}
          />
        </PanelBody>
      </InspectorControls>
      <section className={attributes.sectionClass}>
        <div
          className={`corner ${attributes.cornerPositionClass} ${attributes.sectionClass.replace(
            'section',
            'corner'
          )} ${attributes.cornerBackgroundClass}`}
        ></div>
        <div
          className={`product-hero-wrapper ${isDarkMode ? 'product-landing-page-a' : 'product-landing-page-b'}`}
        >
          <div className={`content ${imgUrl ? 'content-with-image' : ''}`}>
            <RichText
              tagName="p"
              className={'h1-eyebrow'}
              value={intro}
              onChange={(intro) => setAttributes({ intro: intro })}
              placeholder={__('Your intro...')}
            />
            <RichText
              tagName="h1"
              value={header}
              onChange={(header) => setAttributes({ header: header })}
              placeholder={__('Your header...')}
            />
            <div className="description">
              <RichText
                tagName={'p'}
                className="description"
                placeholder={__('Enter description here')}
                value={description}
                onChange={(description) =>
                  setAttributes({ description: description })
                }
              />
            </div>
            <div className="columns">
              {columns.length
                ? columns.map((column, index) => (
                    <div className={'relative'} key={index}>
                      <RichText
                        tagName="div"
                        value={column}
                        onChange={(newContent) => {
                          const newColumns = columns.slice();
                          newColumns[index] = newContent;
                          setAttributes({ columns: newColumns });
                        }}
                        placeholder={`Column ${index + 1}`}
                      />
                      <Button
                        isSmall
                        isPrimary
                        onClick={() => removeColumn(index)}
                        className="removeStaticButton"
                      >
                        <Icon icon="no-alt" /> Remove Column
                      </Button>
                    </div>
                  ))
                : null}
            </div>
            <p className="buttons">
              {buttons.map((button, index) => (
                <div key={`btnIdx-${index}`} className={'relative'}>
                  <PgButton
                    link={button.link}
                    label={button.label}
                    buttonClass={button.buttonClass}
                    onUpdate={(object) => updateButton(index, object)}
                    onRemove={() => removeButton(index)}
                    linkField={'link'}
                    labelField={'label'}
                    buttonClassField={'buttonClass'}
                  />
                </div>
              ))}
            </p>
          </div>
          {imgUrl && <img src={imgUrl} alt={altText} />}
        </div>
      </section>
    </Fragment>
  );
};

registerBlockType('pg/hero-product', {
  title: 'PG Product Hero',
  icon: 'smiley',
  category: 'layout',
  supports: {
    align: true,
    html: false,
  },
  attributes: {
    intro: {
      type: 'string',
      source: 'html',
      selector: 'p.h1-eyebrow',
      default: '',
    },
    header: {
      type: 'string',
      source: 'html',
      selector: 'h1',
      default: '',
    },
    description: {
      type: 'string',
      source: 'html',
      selector: 'div.description',
      default: '',
    },
    columns: {
      type: 'array',
      default: [],
    },
    buttons: {
      type: 'array',
      default: [],
      source: 'query',
      selector: 'p.buttons a',
      query: {
        link: {
          type: 'string',
          source: 'attribute',
          attribute: 'href',
        },
        label: {
          type: 'string',
          source: 'text',
        },
        buttonClass: {
          type: 'string',
          source: 'attribute',
          attribute: 'class',
        },
      },
    },
    imgUrl: {
      type: 'string',
      source: 'attribute',
      attribute: 'src',
      selector: '.product-hero-wrapper img',
    },
    altText: {
      type: 'string',
      default: '',
    },
    isDarkMode: {
      type: 'boolean',
      default: true,
    },
    sectionClass: {
      type: 'string',
      default: 'section-dark-secondary',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-dark-secondary',
    },
  },
  edit: BlockEdit,
  save: ({ attributes }) => {
    const {
      header,
      intro,
      description,
      columns,
      buttons,
      imgUrl,
      altText,
      isDarkMode,
    } = attributes;

    return (
      <section className={attributes.sectionClass}>
        <div
          className={`corner ${attributes.cornerPositionClass} ${attributes.sectionClass.replace(
            'section',
            'corner'
          )} ${attributes.cornerBackgroundClass}`}
        ></div>
        <div
          className={`product-hero-wrapper ${isDarkMode ? 'product-landing-page-a' : 'product-landing-page-b'}`}
        >
          <div className={`content ${imgUrl ? 'content-with-image' : ''}`}>
            <RichText.Content
              tagName="p"
              className={'h1-eyebrow'}
              value={intro}
            />
            <RichText.Content tagName="h1" value={header} />
            <div
              className="description"
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
            <div className="columns">
              {columns.length
                ? columns.map((column, index) => (
                    <div
                      key={index}
                      dangerouslySetInnerHTML={{ __html: column }}
                    ></div>
                  ))
                : null}
            </div>
            <p className="buttons">
              {buttons.map((button, index) => (
                <a
                  key={index}
                  href={button.link}
                  className={button.buttonClass}
                >
                  {button.label}
                </a>
              ))}
            </p>
          </div>
          {imgUrl && <img src={imgUrl} alt={altText} />}
        </div>
      </section>
    );
  },
});
